import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Star, Quote } from "lucide-react";
import { api } from "@/lib/api";
import { useLang } from "@/i18n/LangContext";
import { SITE } from "@/constants/vhm";

const LABELS = {
  nl: { kicker: "Beoordelingen", title: "Wat klanten over ons zeggen", based: (n) => `gebaseerd op ${n} beoordelingen` },
  fr: { kicker: "Avis", title: "Ce que disent nos clients", based: (n) => `sur base de ${n} avis` },
  en: { kicker: "Reviews", title: "What our customers say", based: (n) => `based on ${n} reviews` },
  es: { kicker: "Opiniones", title: "Lo que dicen nuestros clientes", based: (n) => `basado en ${n} opiniones` },
};

export const Stars = ({ n = 5, size = "w-4 h-4" }) => (
  <span className="inline-flex gap-0.5" aria-label={`${n}/5`}>
    {[1, 2, 3, 4, 5].map((i) => (
      <Star key={i} className={`${size} ${i <= n ? "fill-amber-400 text-amber-400" : "text-beto-border"}`} />
    ))}
  </span>
);

export const Reviews = () => {
  const { lang } = useLang();
  const L = LABELS[lang] || LABELS.nl;
  const [data, setData] = useState({ reviews: [], count: 0, average: 0 });

  useEffect(() => {
    api.get("/reviews").then(({ data }) => setData(data)).catch(() => {});
  }, []);

  if (!data.reviews.length) return null;

  const schema = {
    "@context": "https://schema.org",
    "@type": "HousePainter",
    "@id": `${SITE.domain}/#business`,
    name: "VHM Renovation",
    url: `${SITE.domain}/`,
    telephone: "+32499915786",
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: String(data.average),
      reviewCount: String(data.count),
      bestRating: "5",
      worstRating: "1",
    },
    review: data.reviews.slice(0, 12).map((r) => ({
      "@type": "Review",
      author: { "@type": "Person", name: r.author },
      reviewRating: { "@type": "Rating", ratingValue: String(r.rating), bestRating: "5" },
      reviewBody: r.text,
    })),
  };

  return (
    <section id="reviews" className="bg-beto-paper py-20 lg:py-28 border-t border-beto-border" data-testid="reviews-section">
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(schema)}</script>
      </Helmet>
      <div className="max-w-[1300px] mx-auto px-5 lg:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12">
          <div className="max-w-2xl">
            <div className="flex items-center gap-3 mb-4">
              <span className="w-8 h-px bg-beto-primary" />
              <span className="font-body text-xs font-semibold uppercase tracking-[0.25em] text-beto-primary">{L.kicker}</span>
            </div>
            <h2 className="font-heading font-extrabold tracking-tight text-beto-ink text-3xl sm:text-4xl lg:text-5xl leading-tight">
              {L.title}
            </h2>
          </div>
          <div className="flex items-center gap-4 rounded-2xl border border-beto-border bg-beto-surface px-6 py-4" data-testid="reviews-average">
            <p className="font-heading font-extrabold text-4xl text-beto-ink leading-none">{data.average}</p>
            <div>
              <Stars n={Math.round(data.average)} size="w-5 h-5" />
              <p className="mt-1 font-body text-xs text-beto-muted">{L.based(data.count)}</p>
            </div>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {data.reviews.map((r) => (
            <div
              key={r.id}
              data-testid={`review-card-${r.id}`}
              className="relative rounded-2xl border border-beto-border bg-beto-surface p-7 hover:border-beto-primary/50 transition-colors"
            >
              <Quote className="absolute top-6 right-6 w-7 h-7 text-beto-primary/15" />
              <Stars n={r.rating} />
              <p className="mt-4 font-body text-sm md:text-base text-beto-ink leading-relaxed">“{r.text}”</p>
              <div className="mt-6 flex items-center gap-3">
                <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-beto-primary/10 text-beto-primary font-heading font-bold">
                  {r.author?.[0]?.toUpperCase() || "V"}
                </span>
                <div>
                  <p className="font-body font-semibold text-beto-ink text-sm">{r.author}</p>
                  {r.location && <p className="font-body text-xs text-beto-muted">{r.location}</p>}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
